import * as React from 'react';

import {
  enumSalesOrderStatus,
  SalesOrderStatus,
} from '@rematter/frontend-lib-api-client';
import { MultiSelectFilter } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtom } from 'jotai';

import { $columnSelectedStatuses } from '../../../_state/salesOrdersList.state';
import { useTranslations } from '../../../i18n/useTranslations';

interface StatusOption {
  id: SalesOrderStatus;
  label: string;
}

interface StatusColumnFilterProps {
  onClose?: () => void;
}

const getSalesOrderStatusColorClass = (status: SalesOrderStatus): string => {
  switch (status) {
    case enumSalesOrderStatus.Draft:
      return 'bg-neutral/40';
    case enumSalesOrderStatus.Open:
      return 'bg-info';
    case enumSalesOrderStatus.Finished:
      return 'bg-success';
    case enumSalesOrderStatus.Voided:
      return 'bg-failure';
    default:
      return 'bg-neutral/40';
  }
};

export const StatusColumnFilter: React.FC<StatusColumnFilterProps> = ({
  onClose,
}) => {
  const t = useTranslations();
  const [selectedStatusesSet, setSelectedStatusesSet] = useAtom(
    $columnSelectedStatuses,
  );
  const [searchQuery, setSearchQuery] = React.useState('');
  const selectedStatuses = Array.from(selectedStatusesSet);

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedStatuses),
    [selectedStatuses],
  );

  const statusOptions: StatusOption[] = React.useMemo(() => {
    const statusLabels: Record<SalesOrderStatus, string> = {
      [enumSalesOrderStatus.Draft]: t.filters.statusLabels.salesOrder.Draft,
      [enumSalesOrderStatus.Open]: t.filters.statusLabels.salesOrder.Open,
      [enumSalesOrderStatus.Finished]:
        t.filters.statusLabels.salesOrder.Finished,
      [enumSalesOrderStatus.Voided]: t.filters.statusLabels.salesOrder.Voided,
    };

    return Object.values(enumSalesOrderStatus).map((status) => ({
      id: status,
      label: statusLabels[status],
    }));
  }, [t]);

  // Filter status options based on search query
  const filteredStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [statusOptions, searchQuery],
  );

  // Handle selection changes
  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedStatusesSet(new Set(Array.from(keys) as SalesOrderStatus[]));
    },
    [setSelectedStatusesSet],
  );

  // Handle reset
  const handleReset = React.useCallback(() => {
    setSelectedStatusesSet(new Set());
  }, [setSelectedStatusesSet]);

  // Handle search query changes for client-side filtering
  const handleSearch = React.useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  // Custom item renderer for statuses
  const getLabel = React.useCallback(
    (item: StatusOption, isSelected: boolean) => (
      <div className="flex w-full items-center gap-2">
        <div
          className={cn(
            'w-0 grow truncate text-sm',
            isSelected ? 'text-neutral' : 'text-neutral/60',
          )}
        >
          {item.label}
        </div>
        <span
          className={cn(
            'size-2 rounded-full',
            getSalesOrderStatusColorClass(item.id),
          )}
        />
      </div>
    ),
    [],
  );

  // Get selected status options for display
  const selectedStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) => selectedStatuses.includes(option.id)),
    [statusOptions, selectedStatuses],
  );

  return (
    <MultiSelectFilter
      className="@StatusColumnFilter min-w-[250px]"
      entries={filteredStatusOptions}
      selectedEntries={selectedStatusOptions}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(status) => status.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.filters.searchStatuses}
      isLoading={false}
      isFetching={false}
    />
  );
};
